import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getGoogleOAuthUrl } from '../config/oauth'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'

// Password requirements validation
const validatePassword = (password) => ({
  minLength: password.length >= 12,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasNumber: /\d/.test(password),
  hasSpecial: /[!@#$%^&*]/.test(password),
})

const PasswordRequirements = ({ password, isNight }) => {
  const requirements = useMemo(() => validatePassword(password), [password])
  const allMet = Object.values(requirements).every(Boolean)

  const items = [
    { key: 'minLength', label: 'At least 12 characters', met: requirements.minLength },
    { key: 'hasUppercase', label: 'One uppercase letter (A-Z)', met: requirements.hasUppercase },
    { key: 'hasLowercase', label: 'One lowercase letter (a-z)', met: requirements.hasLowercase },
    { key: 'hasNumber', label: 'One number (0-9)', met: requirements.hasNumber },
    { key: 'hasSpecial', label: 'One special character (!@#$%^&*)', met: requirements.hasSpecial },
  ]

  if (!password) return null

  return (
    <div className={`mt-2 p-3 rounded-lg text-xs backdrop-blur-md ${isNight ? 'bg-void-elevated/50 border border-cyber-cyan/10' : 'bg-gray-50/70 border border-gray-200/50'}`}>
      <p className={`font-medium mb-2 ${allMet ? (isNight ? 'text-green-400' : 'text-green-600') : (isNight ? 'text-gray-300' : 'text-gray-700')}`}>
        {allMet ? '✓ Password meets all requirements' : 'Password requirements:'}
      </p>
      <ul className="space-y-1">
        {items.map(({ key, label, met }) => (
          <li key={key} className={`flex items-center gap-2 ${met ? (isNight ? 'text-green-400' : 'text-green-600') : (isNight ? 'text-gray-500' : 'text-gray-500')}`}>
            <span>{met ? '✓' : '○'}</span>
            <span>{label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const Register = () => {
  const { isNight } = useTheme()
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    password: '',
    confirmPassword: '',
    smsConsent: false,
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const { register } = useAuth()
  const navigate = useNavigate()

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setFormData({
      ...formData,
      [e.target.name]: value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    // Validate password meets all requirements
    const passwordReqs = validatePassword(formData.password)
    if (!Object.values(passwordReqs).every(Boolean)) {
      setError('Password does not meet all requirements')
      return
    }

    setLoading(true)

    const result = await register(formData)

    if (result.success) {
      setSuccess(result.message || 'Registration successful! Your account is pending approval.')
      setTimeout(() => {
        navigate('/pending')
      }, 2000)
    } else {
      setError(result.message || 'Registration failed')
    }

    setLoading(false)
  }

  return (
    <div className={`min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 ${isNight ? 'bg-void' : 'bg-gradient-to-b from-gray-50 to-gray-100'}`}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className={`text-center text-3xl font-bold ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
          {isNight ? 'CREATE ACCESS' : 'Create your account'}
        </h2>
        <p className={`mt-2 text-center text-sm ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
          {isNight ? 'Initialize new user credentials' : 'Get access to your customer portal'}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md px-4">
        <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className={`block text-sm font-medium ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  First name *
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`mt-1 ${isNight ? 'glass-input-night' : 'glass-input'}`}
                  placeholder="John"
                />
              </div>

              <div>
                <label htmlFor="lastName" className={`block text-sm font-medium ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  Last name *
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`mt-1 ${isNight ? 'glass-input-night' : 'glass-input'}`}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className={`block text-sm font-medium ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                Email address *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`mt-1 ${isNight ? 'glass-input-night' : 'glass-input'}`}
                placeholder="john@company.com"
              />
            </div>

            <div>
              <label htmlFor="company" className={`block text-sm font-medium ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                Company name *
              </label>
              <input
                id="company"
                name="company"
                type="text"
                required
                value={formData.company}
                onChange={handleChange}
                className={`mt-1 ${isNight ? 'glass-input-night' : 'glass-input'}`}
                placeholder="Your Company Inc."
              />
            </div>

            <div>
              <label htmlFor="phone" className={`block text-sm font-medium ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                Phone number (optional)
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className={`mt-1 ${isNight ? 'glass-input-night' : 'glass-input'}`}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor="password" className={`block text-sm font-medium ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                Password *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className={`mt-1 ${isNight ? 'glass-input-night' : 'glass-input'}`}
                placeholder="••••••••••••"
              />
              <PasswordRequirements password={formData.password} isNight={isNight} />
            </div>

            <div>
              <label htmlFor="confirmPassword" className={`block text-sm font-medium ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                Confirm password *
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`mt-1 ${isNight ? 'glass-input-night' : 'glass-input'}`}
                placeholder="••••••••"
              />
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="smsConsent"
                  name="smsConsent"
                  type="checkbox"
                  checked={formData.smsConsent}
                  onChange={handleChange}
                  className={`h-4 w-4 rounded ${isNight ? 'bg-void-elevated border-void-elevated text-cyber-cyan focus:ring-cyber-cyan' : 'text-primary-600 focus:ring-primary-500 border-gray-300'}`}
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="smsConsent" className={isNight ? 'text-gray-300' : 'text-gray-700'}>
                  I consent to receive SMS notifications from VigilantEdge
                </label>
                <p className={`text-xs mt-1 ${isNight ? 'text-gray-500' : 'text-gray-500'}`}>
                  By checking this box, you agree to receive automated text messages about appointments, service updates, and security notifications. Message frequency varies. Message and data rates may apply. Reply STOP to opt out. View our{' '}
                  <Link to="/privacy-policy" className={isNight ? 'text-cyber-cyan hover:underline' : 'text-primary-600 hover:underline'}>Privacy Policy</Link>
                  {' '}and{' '}
                  <Link to="/terms-of-service" className={isNight ? 'text-cyber-cyan hover:underline' : 'text-primary-600 hover:underline'}>Terms of Service</Link>.
                </p>
              </div>
            </div>

            {error && (
              <div className={`p-3 rounded-lg text-sm backdrop-blur-sm ${isNight ? 'bg-red-900/40 text-red-400 border border-red-500/30' : 'bg-red-50/80 text-red-800 border border-red-200/50'}`}>
                {error}
              </div>
            )}

            {success && (
              <div className={`p-3 rounded-lg text-sm backdrop-blur-sm ${isNight ? 'bg-green-900/40 text-green-400 border border-green-500/30' : 'bg-green-50/80 text-green-800 border border-green-200/50'}`}>
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full disabled:opacity-50 disabled:cursor-not-allowed ${isNight ? 'btn-neon' : 'btn-primary'}`}
            >
              {loading ? 'Creating account...' : isNight ? 'INITIALIZE ACCOUNT' : 'Create account'}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className={`w-full border-t ${isNight ? 'border-cyber-cyan/20' : 'border-gray-200/50'}`}></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-3 ${isNight ? 'bg-transparent text-gray-500' : 'bg-transparent text-gray-500'}`}>
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-6">
              <a
                href={getGoogleOAuthUrl()}
                className={`w-full flex items-center justify-center px-4 py-3 rounded-lg text-sm font-medium transition-all backdrop-blur-md ${
                  isNight
                    ? 'bg-void-elevated/50 border border-cyber-cyan/20 text-gray-300 hover:border-cyber-cyan/50 hover:text-cyber-cyan hover:bg-void-elevated/70'
                    : 'bg-white/50 border border-gray-200/50 text-gray-700 hover:bg-white/70 hover:border-primary-300'
                }`}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign up with Google
              </a>
            </div>

            <p className={`mt-6 text-center text-sm ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
              Already have an account?{' '}
              <Link to="/login" className={`font-medium hover:underline ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}>
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Register
